import { AXIS_HIGH_UNBOUNDED_NUMBER, AXIS_LOW_UNBOUNDED_NUMBER } from 'flow/service/actions';
import type { ActionOption, MatrixModel, OutputValueRange } from '~/dashboard/views/workflows/store/engine/types';
import { updateMatrixSizeToMatchHeaders } from './AxisConfigPopover/helpers';
import type { Axis } from './AxisHeaderDropdown';

export const transformCellValue = (
  matrixDataType: MatrixModel['matrixDataType'],
  value: string,
) => {
  if (!value) return null;

  switch (matrixDataType) {
    case 'number':
      // the user could input a string value in a numeric matrix's cell
      return isNaN(Number(value)) ? null : Number(value);
    case 'string':
      return String(value);
    default:
      return null;
  }
};

export const getShouldDefineValueRanges = (
  outputValueRange: ActionOption,
  defineValueRanges?: boolean,
) => (
  outputValueRange?.jsonValue && (
    defineValueRanges !== false // acceptable values are true or undefined
  )
);

export const getCellColor = ({
  cellValue,
  hasValueRangeConfig,
  outputValueRange,
}: {
  cellValue: number | null;
  hasValueRangeConfig: boolean;
  outputValueRange: OutputValueRange[];
}) => {
  const cellColor = hasValueRangeConfig && typeof cellValue === 'number'
    ? outputValueRange?.find(({ minValue, maxValue }, index, array) => {
      const isLastRange = index === array.length - 1;
      return (minValue === null && maxValue > cellValue)
        || (minValue <= cellValue && (isLastRange ? maxValue >= cellValue : maxValue > cellValue))
        || (minValue <= cellValue && maxValue === null);
    })?.color : null;
  return cellColor;
};

const handleCopyAxisHeader = async ({
  axis, cellIndex,
}: {
  axis: MatrixModel['axis']['x' | 'y'];
  cellIndex: number;
}) => {
  const { axisHeader, order } = axis;

  const clipboardText = order === null
    ? String(axisHeader[cellIndex])
    : `${axisHeader[cellIndex]},${axisHeader[cellIndex + 1]}`;
  const type = 'text/plain';
  const clipboardItemData = {
    [type]: clipboardText,
  };
  const clipboardItem = new ClipboardItem(clipboardItemData);
  await navigator.clipboard.write([clipboardItem]);
};

const detectSeparator = (clipboardText: string) => {
  if (clipboardText.includes('\r\n')) return '\r\n';
  if (clipboardText.includes('\n')) return '\n';
  if (clipboardText.includes('|')) return '|';
  return ',';
};

const getClipboardValuesArray = ({
  axisHeaderDataType,
  clipboardText,
}: {
  axisHeaderDataType: MatrixModel['axis']['x' | 'y']['axisHeaderDataType'];
  clipboardText: string;
}) => {
  const clipboardValuesArray = [];
  const separator = detectSeparator(clipboardText);
  clipboardValuesArray.push(...clipboardText.split(separator));

  return axisHeaderDataType === 'number'
    ? clipboardValuesArray.map(textValue => Number(textValue))
    : clipboardValuesArray;
};

const handlePasteAxisHeader = async ({
  axis, cellIndex, handleUpdateMatrixModel, matrixModel,
}: {
  axis: Axis;
  matrixModel: MatrixModel;
  cellIndex: number;
  handleUpdateMatrixModel: (newMatrix: MatrixModel) => void;
}) => {
  const axisPath = axis.toLowerCase();
  const { axisHeader, axisHeaderDataType, order } = matrixModel.axis[axisPath];
  const clipboardText = await navigator.clipboard.readText();

  const updatedAxisHeader = [...axisHeader];
  const clipboardValuesArray = getClipboardValuesArray({ clipboardText, axisHeaderDataType });

  const isPastingInvalidNumbers = (
    axisHeaderDataType === 'number'
    && clipboardValuesArray.some(val => Number.isNaN(Number(val)))
  );
  if (isPastingInvalidNumbers) return;

  // special handling for an axis with range configurations
  if (order) {
    if (clipboardValuesArray.length !== 2) return;
    const unboundValues = [
      AXIS_LOW_UNBOUNDED_NUMBER, AXIS_HIGH_UNBOUNDED_NUMBER,
    ] as Array<string | number>;
    const firstValueUnbound = unboundValues.includes(clipboardValuesArray[0]);
    const lastValueUnbound = unboundValues.includes(clipboardValuesArray[1]);
    const pastingIntoFirstRow = cellIndex === 0;
    const pastingIntoLastRow = cellIndex === updatedAxisHeader.length - 2;

    if (firstValueUnbound && !pastingIntoFirstRow) {
      updatedAxisHeader.splice(cellIndex + 1, 1, clipboardValuesArray[1]);
    } else if (lastValueUnbound && !pastingIntoLastRow) {
      updatedAxisHeader.splice(cellIndex + 1, 1, clipboardValuesArray[0]);
    } else {
      updatedAxisHeader.splice(cellIndex, 2, ...clipboardValuesArray);
    }
  } else {
    updatedAxisHeader.splice(cellIndex, clipboardValuesArray.length, ...clipboardValuesArray);
  }

  const updatedMatrix = updateMatrixSizeToMatchHeaders({
    axis,
    axisSettings: {
      ...matrixModel.axis[axisPath],
      axisHeader: updatedAxisHeader,
    },
    matrix: matrixModel.matrix,
  });

  const updatedMatrixModel = {
    ...matrixModel,
    axis: {
      ...matrixModel.axis,
      [axisPath]: {
        ...matrixModel.axis[axisPath],
        axisHeader: updatedAxisHeader,
      },
    },
    matrix: updatedMatrix,
  };
  handleUpdateMatrixModel(updatedMatrixModel);
};

export const handleCopyOrPaste = async ({
  event,
  copyHandler,
  pasteHandler,
}) => {
  const { key, ctrlKey, metaKey } = event;
  if (ctrlKey || metaKey) {
    if (key === 'c') {
      copyHandler();
    } else if (key === 'v') {
      pasteHandler();
    }
  }
};

export const handleCopyOrPasteAxisHeader = async ({
  axis, cellIndex, event, handleUpdateMatrixModel, matrixModel,
}: {
  axis: Axis;
  cellIndex: number;
  event: React.KeyboardEvent;
  handleUpdateMatrixModel: (newMatrix: MatrixModel) => void;
  matrixModel: MatrixModel;
}) => {
  handleCopyOrPaste({
    event,
    copyHandler: () => {
      const { axisHeader, order } = matrixModel.axis[axis.toLowerCase()];
      const invalidCopy = (
        axisHeader[cellIndex] === null
        || (order !== null && axisHeader[cellIndex + 1] === null)
      );
      if (invalidCopy) return;

      handleCopyAxisHeader({ axis: matrixModel.axis[axis.toLowerCase()], cellIndex });
    },
    pasteHandler: () => {
      handlePasteAxisHeader({
        axis, cellIndex, handleUpdateMatrixModel, matrixModel,
      });
    },
  });
};
